/**
 * UniversalInviteModal (Iter 9.47.s scaffold)
 *
 * One modal that Naomi (or any host_management owner) opens to invite a new
 * Pilot OR Subscriber into a category. Toggle at the top switches between the
 * two flows. Both flows generate a magic invite link the host can copy.
 *
 * Per user spec:
 *   - 2 copy-link buttons per pipeline row (this is the modal triggered when
 *     "Invite" is clicked from a pipeline row, or the global Invite CTA)
 *   - Pilot path: NDA + pilot-legacy extras
 *   - Subscriber path: standard envelope → welcome → intake
 *
 * Backend endpoint (existing, reused):
 *   POST /api/admin/host-applications/invite
 *      body: { email, brand_name, category, invite_type: "pilot" | "subscriber",
 *              custom_envelope: {...}, pilot_extras: {...} }
 *      returns: { invite_url, pilot_url, public_url }
 *
 * Iter 9.47.t will wire the actual envelope→intake flow that this link opens.
 */
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, UserPlus, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CopyButton from "@/components/CopyButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INVITE_TYPES = [
  { id: "pilot",      label: "Pilot",      desc: "Free legacy access + NDA. For seed hosts.", icon: Crown },
  { id: "subscriber", label: "Subscriber", desc: "Standard paid plan. Goes through public intake.", icon: Sparkles },
];

export default function UniversalInviteModal({
  open,
  onOpenChange,
  category,
  prefilledEmail = "",
  prefilledBrand = "",
  onInviteSent,
}) {
  const [inviteType, setInviteType] = useState("pilot");
  const [email, setEmail] = useState(prefilledEmail);
  const [brandName, setBrandName] = useState(prefilledBrand);
  const [city, setCity] = useState("");
  const [envelopeNote, setEnvelopeNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setEmail(prefilledEmail);
    setBrandName(prefilledBrand);
    setCity("");
    setEnvelopeNote("");
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!email || !brandName) {
      toast.error("Email and brand name required");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("session_token");
      const r = await fetch(`${API}/admin/host-applications/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          email,
          brand_name: brandName,
          city,
          category,
          invite_type: inviteType,
          custom_envelope: envelopeNote ? { note: envelopeNote } : undefined,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Invite failed");
      setResult(data);
      onInviteSent?.(data);
      toast.success(`${inviteType === "pilot" ? "Pilot" : "Subscriber"} invite created`);
    } catch (err) {
      toast.error(err.message || "Failed to send invite");
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = undefined; // legacy local fn removed - CopyButton handles copy state

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange?.(o); }}>
      <DialogContent className="max-w-md" data-testid="universal-invite-modal">
        <DialogHeader>
          <DialogTitle>Invite to {category || "category"}</DialogTitle>
          <DialogDescription>
            Send a magic invite. Toggle pilot or subscriber to choose the onboarding shape.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4 mt-2">
            {/* Invite type toggle */}
            <div className="grid grid-cols-2 gap-2" data-testid="invite-type-toggle">
              {INVITE_TYPES.map((t) => {
                const Icon = t.icon;
                const active = inviteType === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setInviteType(t.id)}
                    data-testid={`invite-type-${t.id}`}
                    className={`text-left p-3 rounded-xl border-2 transition ${
                      active ? "border-stone-900 bg-stone-50" : "border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${active ? "text-stone-900" : "text-stone-500"}`} />
                      <span className="text-sm font-medium">{t.label}</span>
                    </div>
                    <p className="text-[10px] text-stone-500 leading-tight">{t.desc}</p>
                  </button>
                );
              })}
            </div>

            {/* Form */}
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="host@example.com"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Brand name</label>
              <Input
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Aria James Studio"
                data-testid="invite-brand-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">City <span className="text-gray-400">(optional)</span></label>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Atlanta, GA"
                data-testid="invite-city-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Envelope note <span className="text-gray-400">(optional, shown on welcome page)</span></label>
              <Textarea
                rows={3}
                value={envelopeNote}
                onChange={(e) => setEnvelopeNote(e.target.value)}
                placeholder="Hey Aria - excited to bring you in early. Let's get your makeup studio set up."
                data-testid="invite-envelope-note-input"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={() => onOpenChange?.(false)} disabled={submitting} data-testid="invite-cancel-btn">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-stone-900 hover:bg-stone-800 text-white"
                data-testid="invite-submit-btn"
              >
                {submitting && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
                <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                Create invite
              </Button>
            </div>
          </div>
        ) : (
          // Success: show 2 copy-link buttons (per user spec - 2 buttons per row)
          <div className="space-y-3 mt-2" data-testid="invite-result-block">
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-xs text-green-800">
              Invite created. Share one of these links with {email}.
            </div>
            <CopyLinkRow
              label="Magic invite link"
              url={result.invite_url}
            />
            <CopyLinkRow
              label="Public preview link"
              url={result.public_url || result.pilot_url || result.invite_url}
            />
            <div className="flex justify-end pt-2 border-t">
              <Button size="sm" variant="outline" onClick={() => onOpenChange?.(false)} data-testid="invite-done-btn">
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CopyLinkRow({ label, url }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-white">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
        <p className="text-xs text-gray-800 truncate font-mono">{url}</p>
      </div>
      <CopyButton
        value={url}
        iconOnly
        successMessage={`${label} copied`}
        testId={`copy-link-${label.replace(/\s+/g, "-").toLowerCase()}`}
      />
    </div>
  );
}
